import { projectQueryOptions, useAssociateProjectTenantMutation } from "#api/migrate-web/routes/projects.ts";
import { migrateAssessmentTenantIdStorage, pendingProjectConsentStorage } from "@sg-protect/core";
import { TenantProvider } from "@sg-protect/shell";
import { useSuspenseQuery } from "@tanstack/react-query";
import { type PropsWithChildren, createContext, useContext, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { useForbiddenTenantRedirect } from "#hooks/useForbiddenTenantRedirect.ts";
import { type ProjectTenantView, deriveProjectTenantState } from "./deriveProjectTenantState.ts";
import { useRenameProjectToTenantName } from "./useRenameProjectToTenantName.ts";

const ProjectTenantViewContext = createContext<ProjectTenantView>("resolved");

export function useProjectTenantView() {
    return useContext(ProjectTenantViewContext);
}

export function ProjectTenantProvider({ children }: PropsWithChildren) {
    const { projectId = "" } = useParams();

    const { data: project, isFetching: isProjectFetching } = useSuspenseQuery(projectQueryOptions(projectId));
    const associateTenant = useAssociateProjectTenantMutation();

    const [pending] = useState(() => pendingProjectConsentStorage.get());
    const pendingTenantId = pending?.projectId === projectId ? pending.tenantId : undefined;

    const tenantId = project.tenantId ?? null;
    const hasTenant = tenantId !== null;
    const tenants = useMemo(() => (tenantId ? [{ tenantId }] : []), [tenantId]);
    const isRedirectInstalled = useForbiddenTenantRedirect();

    const { view, action } = deriveProjectTenantState({
        hasTenant,
        pendingTenantId,
        isProjectFetching,
        isAssociateError: associateTenant.isError,
        isAssociateSuccess: associateTenant.isSuccess,
        isAssociatePending: associateTenant.isPending
    });

    useRenameProjectToTenantName(projectId, pendingTenantId, hasTenant);

    useEffect(() => {
        if (action === "clear") {
            pendingProjectConsentStorage.clear();
        } else if (action === "setProject") {
            pendingProjectConsentStorage.setProject(projectId);
        } else if (action === "mutate" && pendingTenantId !== undefined) {
            associateTenant.mutate({ params: { path: { projectId } }, body: { tenantId: pendingTenantId } });
        }
    }, [action, pendingTenantId, projectId, associateTenant]);

    return (
        <TenantProvider key={tenantId} tenants={tenants} storage={migrateAssessmentTenantIdStorage}>
            <ProjectTenantViewContext.Provider value={view}>
                {isRedirectInstalled ? children : null}
            </ProjectTenantViewContext.Provider>
        </TenantProvider>
    );
}
